/**
 * App Component
 * Main application with minimal fixed navigation
 */

import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from 'sonner';
import { AppProvider, useApp } from './contexts/AppContext';
import { Logo } from './components/layout/Logo';
import { ThemeToggle } from './components/layout/ThemeToggle';
import { StoreLink } from './components/StoreLink';
import { AboutSidebar } from './components/layout/AboutSidebar';
import { GetTheAppBanner } from './components/GetTheAppBanner';
import { FirstVisitIntro } from './components/FirstVisitIntro';
import { Button } from './components/ui/Button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './components/ui/Tooltip';
import { HomePage } from './pages/HomePage';
import { BookPage } from './pages/BookPage';
import { ShopPage } from './pages/ShopPage';
import { CollectionPage } from './pages/CollectionPage';
import { GalleryPage } from './pages/GalleryPage';
import { ArtistProfilePage } from './pages/ArtistProfilePage';
import { PromptsPage } from './pages/PromptsPage';
import { UserDrawPage } from './pages/UserDrawPage';
import { TermsPage } from './pages/TermsPage';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { AdminDrawPage } from './pages/AdminDrawPage';
import { AdminApprovalsPage } from './pages/AdminApprovalsPage';
import { AdminPromptsPage } from './pages/AdminPromptsPage';
import { AdminContentPage } from './pages/AdminContentPage';
import { AdminSharePage } from './pages/AdminSharePage';
import { AdminActivityPage } from './pages/AdminActivityPage';
import { AdminZinePage } from './pages/AdminZinePage';
import { AdminEmailsPage } from './pages/AdminEmailsPage';
import {
  Palette,
  LogOut,
  List,
  CheckSquare,
  Settings,
  Share2,
  Activity,
  BookOpen,
  Mail,
  X,
} from 'lucide-react';
import { useCallback, useState } from 'react';

/**
 * Navigation Button Component
 */
function NavButton({
  to,
  isActive,
  onClick,
  icon,
  tooltip,
  variant = 'default',
}: {
  to: string;
  isActive: boolean;
  onClick?: () => void;
  icon: React.ReactNode;
  tooltip: string;
  variant?: 'default' | 'destructive';
}) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(to);
    }
  };

  const className =
    variant === 'destructive'
      ? 'text-red-500 hover:text-red-600 w-10 h-10 md:w-12 md:h-12 p-0 bg-transparent hover:bg-transparent'
      : isActive
      ? 'text-foreground w-10 h-10 md:w-12 md:h-12 p-0 bg-transparent hover:bg-transparent'
      : 'text-foreground w-10 h-10 md:w-12 md:h-12 p-0 bg-transparent hover:bg-transparent';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button onClick={handleClick} variant="ghost" className={className}>
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Fixed Navigation Component
 */
function FixedNav() {
  const { isAdmin, logout } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isDrawingPage = location.pathname === '/user-draw' || location.pathname === '/admin/draw';

  const handleLogoClick = useCallback(() => {
    // If on home page, toggle sidebar; otherwise navigate home
    if (location.pathname === '/') {
      setIsSidebarOpen(prev => !prev);
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  return (
    <>
      {/* About Sidebar */}
      <AboutSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Logo in top-left — hidden in the drawing tool's focus mode */}
      {!isDrawingPage && <Logo onClick={handleLogoClick} isOpen={isSidebarOpen} />}

      {/* Drawing tool: focus mode — no header or nav, just a close (X) button */}
      {isDrawingPage && (
        <button
          onClick={() => navigate(-1)}
          aria-label="Close drawing tool"
          className="fixed top-3 right-3 md:top-4 md:right-4 z-[9999] w-11 h-11 rounded-full flex items-center justify-center text-foreground hover:bg-accent transition-colors"
          style={{ marginTop: 'var(--pb-banner-h, 0px)' }}
        >
          <X className="w-7 h-7" />
        </button>
      )}

      {/* Navigation icons in top-right — hidden in the drawing tool's focus mode */}
      {!isDrawingPage && (
      <div className="fixed top-2 right-2 md:top-4 md:right-4 flex flex-wrap gap-1.5 md:gap-4 z-[9999] max-w-[calc(100vw-80px)] md:max-w-none justify-end" style={{ marginTop: 'var(--pb-banner-h, 0px)' }}>
        {/* Public navigation */}
        <NavButton
          to="/prompts"
          isActive={location.pathname === '/prompts'}
          icon={
            <svg className="w-10 h-10 md:w-12 md:h-12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66.39 67.63" fill="currentColor">
              <rect x="20.25" y="14.09" width="30.88" height="1"/>
              <rect x="20.25" y="23.71" width="30.88" height="1"/>
              <rect x="20.25" y="33.34" width="30.88" height="1"/>
              <rect x="20.25" y="42.96" width="30.88" height="1"/>
              <rect x="20.25" y="52.59" width="30.88" height="1"/>
              <path d="M15.34,15.59h-.7c-.55,0-1-.45-1-1s.45-1,1-1h.7c.55,0,1,.45,1,1s-.45,1-1,1Z"/>
              <path d="M15.34,25.21h-.7c-.55,0-1-.45-1-1s.45-1,1-1h.7c.55,0,1,.45,1,1s-.45,1-1,1Z"/>
              <path d="M15.34,34.84h-.7c-.55,0-1-.45-1-1s.45-1,1-1h.7c.55,0,1,.45,1,1s-.45,1-1,1Z"/>
              <path d="M15.34,44.46h-.7c-.55,0-1-.45-1-1s.45-1,1-1h.7c.55,0,1,.45,1,1s-.45,1-1,1Z"/>
              <path d="M15.34,54.09h-.7c-.55,0-1-.45-1-1s.45-1,1-1h.7c.55,0,1,.45,1,1s-.45,1-1,1Z"/>
            </svg>
          }
          tooltip="Browse Prompts"
        />
        <NavButton
          to="/gallery"
          isActive={location.pathname === '/gallery'}
          icon={
            <svg className="w-10 h-10 md:w-12 md:h-12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66.39 67.63" fill="currentColor">
              <rect x="15.95" y="13.09" width="8.52" height="11.22"/>
              <rect x="28.17" y="13.09" width="8.52" height="11.22"/>
              <rect x="40.38" y="13.09" width="8.52" height="11.22"/>
              <rect x="15.95" y="27.9" width="8.52" height="11.22"/>
              <rect x="28.17" y="27.9" width="8.52" height="11.22"/>
              <rect x="40.38" y="27.9" width="8.52" height="11.22"/>
              <rect x="15.95" y="42.7" width="8.52" height="11.22"/>
              <rect x="28.17" y="42.7" width="8.52" height="11.22"/>
              <rect x="40.38" y="42.7" width="8.52" height="11.22"/>
            </svg>
          }
          tooltip="Gallery"
        />

        {/* Admin navigation */}
        {isAdmin && (
          <>
            <NavButton
              to="/admin/prompts"
              isActive={location.pathname === '/admin/prompts'}
              icon={<List className="w-10 h-10 md:w-12 md:h-12" />}
              tooltip="Manage Prompts"
            />
            <NavButton
              to="/admin/approvals"
              isActive={location.pathname === '/admin/approvals'}
              icon={<CheckSquare className="w-10 h-10 md:w-12 md:h-12" />}
              tooltip="Approve Artworks"
            />
            <NavButton
              to="/admin/draw"
              isActive={location.pathname === '/admin/draw'}
              icon={<Palette className="w-10 h-10 md:w-12 md:h-12" />}
              tooltip="Draw"
            />
            <NavButton
              to="/admin/share"
              isActive={location.pathname === '/admin/share'}
              icon={<Share2 className="w-10 h-10 md:w-12 md:h-12" />}
              tooltip="Share Images"
            />
            <NavButton
              to="/admin/content"
              isActive={location.pathname === '/admin/content'}
              icon={<Settings className="w-10 h-10 md:w-12 md:h-12" />}
              tooltip="Site Content"
            />
            <NavButton
              to="/admin/activity"
              isActive={location.pathname === '/admin/activity'}
              icon={<Activity className="w-10 h-10 md:w-12 md:h-12" />}
              tooltip="Activity"
            />
            <NavButton
              to="/admin/zine"
              isActive={location.pathname === '/admin/zine'}
              icon={<BookOpen className="w-10 h-10 md:w-12 md:h-12" />}
              tooltip="Zine 2.0"
            />
            <NavButton
              to="/admin/emails"
              isActive={location.pathname === '/admin/emails'}
              icon={<Mail className="w-10 h-10 md:w-12 md:h-12" />}
              tooltip="Participant emails"
            />
            <NavButton
              to="/"
              isActive={false}
              onClick={logout}
              icon={<LogOut className="w-10 h-10 md:w-12 md:h-12" />}
              tooltip="Logout"
              variant="destructive"
            />
          </>
        )}
      </div>
      )}

      {/* Theme toggle in bottom-left */}
      <div className="fixed bottom-4 left-4 z-[9999]">
        <ThemeToggle />
      </div>

      {/* The way into the store, bottom-right */}
      <StoreLink />
    </>
  );
}

/**
 * Protected route wrapper for admin pages
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useApp();

  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}

/**
 * App routes with minimal layout
 */
function AppRoutes() {
  return (
    <>
      <GetTheAppBanner />
      <FirstVisitIntro />
      <div className="h-screen bg-background" style={{ paddingTop: 'var(--pb-banner-h, 0px)' }}>
        <FixedNav />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/book" element={<BookPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/collection" element={<CollectionPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/artist/:alias" element={<ArtistProfilePage />} />
        <Route path="/prompts" element={<PromptsPage />} />
        <Route path="/user-draw" element={<UserDrawPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route
          path="/admin/prompts"
          element={
            <ProtectedRoute>
              <AdminPromptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/approvals"
          element={
            <ProtectedRoute>
              <AdminApprovalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/draw"
          element={
            <ProtectedRoute>
              <AdminDrawPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/content"
          element={
            <ProtectedRoute>
              <AdminContentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/share"
          element={
            <ProtectedRoute>
              <AdminSharePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/activity"
          element={
            <ProtectedRoute>
              <AdminActivityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/zine"
          element={
            <ProtectedRoute>
              <AdminZinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/emails"
          element={
            <ProtectedRoute>
              <AdminEmailsPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </div>
    </>
  );
}

/**
 * Main App with providers
 */
export function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <AppProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-center" />
          </BrowserRouter>
        </AppProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}
